import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Box
} from "@mui/material";
import Page from "../components/Page";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import { fetchallcitylist, fetchchapterbyuser } from "src/utils/common";
import { getRenewalFollowupReport } from "src/api/commonapi";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import Label from "src/components/Label";
import CustomLoader from "src/commonComponents/CustomLoader";

const windowOptions = [
  { value: "all", label: "All Windows" },
  { value: "30-day", label: "30-Day" },
  { value: "60-day", label: "60-Day" }
];

const statusColor = (status) => {
  if (status === "missed") return "error";
  if (status === "pending") return "warning";
  if (status === "completed") return "success";
  return "default";
};

const statusLabel = (status) => {
  if (status === "no-log") return "No Log";
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

// Backend sends follow-up datetimes as-is in "DD-MM-YYYY hh:mm A" (contract §3 R6).
// Parse with that format for display/sort; empty string means no follow-up yet.
const parseFollowupDate = (v) => {
  if (!v) return 0;
  const m = moment(v, "DD-MM-YYYY hh:mm A");
  return m.isValid() ? m.valueOf() : 0;
};

// created_at arrives as "YYYY-MM-DD HH:mm:ss" — display it like the other
// follow-up dates ("DD-MM-YYYY hh:mm A").
const formatLoggedAt = (v) => {
  if (!v) return "-";
  const m = moment(v, "YYYY-MM-DD HH:mm:ss");
  return m.isValid() ? m.format("DD-MM-YYYY hh:mm A") : v;
};

export default function RenewalFollowupReport() {
  let loginUser = JSON.parse(localStorage.getItem("userData"));
  const navigate = useNavigate();

  // Admin-only page (same role split as SidebarConfig/routes): city users
  // (u_type == 1) and plain staff never get this sidebar entry or route,
  // but guard direct URL access too.
  const hasAccess =
    loginUser?.u_type == 0 ||
    (loginUser?.p_user_id == 1 && loginUser?.u_type == 2 && loginUser?.is_subadmin == 1) ||
    (loginUser?.p_user_id == 1 && loginUser?.u_type == 2 && loginUser?.management_p != 0);

  useEffect(() => {
    if (!hasAccess) navigate("/dashboard", { replace: true });
  }, [hasAccess]);

  const lockedCity =
    loginUser?.u_type == 1 ||
    (loginUser?.u_type == 2 && loginUser?.p_user_id != 1 && loginUser?.management_p == 1);

  const [chaptlist, setchaplist] = useState([]);
  const [citylist, setcitylist] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [newTableData, setNewTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const firstFetch = useRef(true);

  const [searchdata, setsearchdata] = useState({
    city_id: lockedCity ? loginUser?.city_id : 0,
    chpt_id: 0,
    window: "all",
    from_date: "",
    to_date: "",
    search: "",
    tab: "all"
  });

  const counts = {
    all: tableData?.length || 0,
    pending: tableData?.filter((r) => r.followup_status === "pending")?.length || 0,
    missed:
      tableData?.filter((r) => r.followup_status === "missed" || r.is_missed == 1)?.length || 0,
    completed: tableData?.filter((r) => r.followup_status === "completed")?.length || 0,
    "no-log": tableData?.filter((r) => r.followup_status === "no-log")?.length || 0
  };

  const fetchReport = async (filters) => {
    setIsLoading(true);
    try {
      // status is always "all" — tabs filter client-side on the returned
      // followup_status / is_missed flags (per backend contract §2).
      const res = await getRenewalFollowupReport({
        city_id: filters.city_id,
        chpt_id: filters.chpt_id,
        window: filters.window,
        status: "all",
        from_date: filters.from_date || "",
        to_date: filters.to_date || "",
        search: (filters.search || "").trim()
      });
      if (res?.status == 1) {
        setTableData(res?.data || []);
      } else {
        setTableData([]);
      }
    } catch (error) {
      setTableData([]);
    }
    setIsLoading(false);
  };

  // Debounced server fetch on filter change (tab switches filter client-side only).
  useEffect(() => {
    const delay = firstFetch.current ? 0 : 400;
    firstFetch.current = false;
    const t = setTimeout(() => {
      fetchReport(searchdata);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchdata.city_id,
    searchdata.chpt_id,
    searchdata.window,
    searchdata.from_date,
    searchdata.to_date,
    searchdata.search
  ]);

  // Client-side tab filter (All | Pending | Missed | Completed | No Log, per backend
  // contract §3 R2). Derivation order is server-side (no-log, then completed, first).
  useEffect(() => {
    if (searchdata.tab === "all") {
      setNewTableData(tableData);
    } else if (searchdata.tab === "missed") {
      setNewTableData(
        tableData?.filter((r) => r.followup_status === "missed" || r.is_missed == 1)
      );
    } else {
      setNewTableData(tableData?.filter((r) => r.followup_status === searchdata.tab));
    }
  }, [searchdata.tab, tableData]);

  useEffect(async () => {
    setchaplist(await fetchchapterbyuser(loginUser.city_id, loginUser.u_type));
    const cities = await fetchallcitylist();
    if (cities) setcitylist(cities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ClearSearchResult = () => {
    setsearchdata({
      city_id: lockedCity ? loginUser?.city_id : 0,
      chpt_id: 0,
      window: "all",
      from_date: "",
      to_date: "",
      search: "",
      tab: "all"
    });
  };

  const expo_columns = [
    { label: "Member ID", key: "m_id" },
    { label: "Member Name", key: "m_name" },
    { label: "Mobile", key: "mobile" },
    { label: "Chapter", key: "chpt_name" },
    { label: "City", key: "city_name" },
    { label: "Renewal Due Date", key: "renewal_due_date" },
    { label: "Window", key: "window" },
    { label: "Status", key: "followup_status" },
    { label: "Follow-up Count", key: "followup_count" },
    { label: "Last Follow-up Date", key: "last_followup_date" },
    { label: "Next Follow-up Date", key: "next_followup_date" },
    { label: "Last Follow-up By", key: "last_followup_by" }
  ];

  const columns = [
    {
      name: "No",
      selector: (row) => row.m_id,
      wrap: true,
      sortable: true,
      width: "80px"
    },
    {
      name: "Member Name",
      selector: (row) => row.m_name,
      wrap: true,
      sortable: true,
      width: "180px"
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile,
      wrap: true,
      width: "130px"
    },
    {
      name: "Chapter",
      selector: (row) => row.chpt_name,
      wrap: true,
      sortable: true,
      width: "130px"
    },
    {
      name: "Renewal Due",
      selector: (row) => row.renewal_due_date,
      wrap: false,
      sortable: false,
      width: "120px"
    },
    {
      name: "Window",
      selector: (row) => row.window,
      wrap: true,
      sortable: true,
      width: "100px"
    },
    {
      name: "Last Follow-up",
      selector: (row) => row.last_followup_date || "-",
      wrap: true,
      width: "200px"
    },
    {
      name: "Next Follow-up",
      selector: (row) => row.next_followup_date || "-",
      wrap: true,
      sortable: true,
      sortFunction: (a, b) => parseFollowupDate(a.next_followup_date) - parseFollowupDate(b.next_followup_date),
      width: "200px"
    },
    {
      name: "Follow-up By",
      selector: (row) => row.last_followup_by || "-",
      wrap: true,
      width: "140px"
    },
    {
      name: "Count",
      selector: (row) => row.followup_count,
      sortable: true,
      width: "80px"
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => (
        <Label variant="ghost" color={statusColor(row.followup_status)}>
          {statusLabel(row.followup_status)}
        </Label>
      )
    }
  ];

  // View-only expandable history (same UX as MeetingFeedbackReport ExpandableRow).
  const ExpandableRow = ({ data }) => {
    return (
      <div style={{ padding: "10px 15px", backgroundColor: "#f3f3f39c" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Follow-up History ({data?.history?.length || 0})
        </Typography>
        {(data?.history || []).map((h) => (
          <Card key={h.id} sx={{ p: 1.5, mb: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Follow-up Date
                </Typography>
                <Typography variant="body2">{h.followup_datetime}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Taken By
                </Typography>
                <Typography variant="body2">
                  {h.taken_by}
                  {h.taken_by_role ? ` (${h.taken_by_role})` : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Next Follow-up
                </Typography>
                <Typography variant="body2">{h.next_followup_date}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">
                  Logged At
                </Typography>
                <Typography variant="body2">{formatLoggedAt(h.created_at)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Member Response
                </Typography>
                <Typography variant="body2">{h.response}</Typography>
              </Grid>
            </Grid>
          </Card>
        ))}
      </div>
    );
  };

  if (!hasAccess) return null;

  return (
    <>
      <Page title="Renewal Follow-up By LT | Local Vocal Business Group">
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" gutterBottom>
              Renewal Follow-up By LT
            </Typography>
          </Stack>

          <Card className="p-20">
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Stack>
                  <label>Select City</label>
                  <Select
                    menuPortalTarget={document.body}
                    isDisabled={lockedCity}
                    options={[
                      { value: 0, label: "All City" },
                      ...citylist.map((city) => ({
                        label: city?.city_name,
                        value: city?.city_id
                      }))
                    ]}
                    onChange={(e) => {
                      setsearchdata({ ...searchdata, city_id: e.value, chpt_id: 0 });
                    }}
                    value={
                      searchdata.city_id == 0
                        ? { value: 0, label: "All City" }
                        : citylist.map((city) => {
                            return (
                              searchdata.city_id == city.city_id && {
                                value: city.city_id,
                                label: city.city_name
                              }
                            );
                          })
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={3}>
                <Stack>
                  <label>Select Chapter</label>
                  <Select
                    name="chpt_id"
                    menuPortalTarget={document.body}
                    onChange={(e) => {
                      setsearchdata({ ...searchdata, chpt_id: e.value });
                    }}
                    options={[
                      { value: 0, label: "All Chapter" },
                      ...(chaptlist &&
                        chaptlist
                          ?.map((chpt) => {
                            if (searchdata?.city_id != 0) {
                              if (searchdata?.city_id == chpt?.city_id) {
                                return {
                                  value: chpt.chpt_id,
                                  label: chpt.chpt_name
                                };
                              }
                            } else {
                              return {
                                value: chpt.chpt_id,
                                label: chpt.chpt_name
                              };
                            }
                          })
                          .filter((f) => f != undefined))
                    ]}
                    value={
                      searchdata.chpt_id == 0
                        ? { value: 0, label: "All Chapter" }
                        : chaptlist &&
                          chaptlist.map((c) => {
                            if (c.chpt_id == searchdata.chpt_id) {
                              return {
                                value: c.chpt_id,
                                label: c.chpt_name
                              };
                            }
                          })
                    }
                  />
                </Stack>
              </Grid>

              <Grid item xs={3}>
                <Stack>
                  <label>Select Window</label>
                  <Select
                    menuPortalTarget={document.body}
                    value={windowOptions.find((o) => o.value === searchdata.window)}
                    onChange={(e) => {
                      setsearchdata({ ...searchdata, window: e.value });
                    }}
                    options={windowOptions}
                  />
                </Stack>
              </Grid>

              <Grid item xs={3}>
                <Stack>
                  <label>Search (Name / Mobile)</label>
                  <TextField
                    size="small"
                    placeholder="Search member"
                    value={searchdata.search}
                    onChange={(e) => {
                      setsearchdata({ ...searchdata, search: e.target.value });
                    }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={3}>
                <Stack>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      type="date"
                      label="Due From "
                      inputFormat="dd/MM/yyyy"
                      value={
                        searchdata.from_date
                          ? moment(searchdata.from_date, "YYYY-MM-DD").toDate()
                          : null
                      }
                      onChange={(newDate) => {
                        setsearchdata({
                          ...searchdata,
                          from_date: newDate ? moment(newDate).format("YYYY-MM-DD") : ""
                        });
                      }}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>

              <Grid item xs={3}>
                <Stack>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      type="date"
                      label="Due To "
                      inputFormat="dd/MM/yyyy"
                      value={
                        searchdata.to_date
                          ? moment(searchdata.to_date, "YYYY-MM-DD").toDate()
                          : null
                      }
                      onChange={(newDate) => {
                        setsearchdata({
                          ...searchdata,
                          to_date: newDate ? moment(newDate).format("YYYY-MM-DD") : ""
                        });
                      }}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>

              <Grid item xs={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-between"
                  }}
                >
                  <Button variant="outlined" onClick={ClearSearchResult}>
                    Clear
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Card>

          <Card style={{ marginTop: "15px" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={searchdata.tab}
                onChange={(e, v) => setsearchdata({ ...searchdata, tab: v })}
              >
                <Tab value="all" label={`All (${counts.all})`} />
                <Tab value="pending" label={`Pending (${counts.pending})`} />
                <Tab value="missed" label={`Missed (${counts.missed})`} />
                <Tab value="completed" label={`Completed (${counts.completed})`} />
                <Tab value="no-log" label={`No Log (${counts["no-log"]})`} />
              </Tabs>
            </Box>
            <Button className="btn btn-export">
              <CSVLink
                data={newTableData != null ? newTableData : []}
                headers={expo_columns}
                filename={"Renewal Followup Data.csv"}
                className="btn btn-primary test"
                target="_blank"
              >
                Export CSV
              </CSVLink>
            </Button>
            <DataTable
              style={{ overflow: "hidden" }}
              filter
              pagination
              highlightOnHover
              columns={columns}
              data={newTableData != null ? newTableData : []}
              expandableRows
              expandableRowsComponent={ExpandableRow}
              expandableRowDisabled={(row) => !row?.history?.length}
              conditionalRowStyles={[
                {
                  when: (row) => row?.is_missed == 1,
                  style: {
                    backgroundColor: "#fdecea"
                  }
                }
              ]}
              progressPending={isLoading}
              progressComponent={<CustomLoader />}
            />
          </Card>
        </Container>
      </Page>
    </>
  );
}
